import takeRight from 'lodash/takeRight';
import * as React from 'react';
import { SKELETON_CLASSNAME } from '../../base/constants';
import { formatDate, printDayMonth } from '../../base/dateUtils';
import DummyProgress from './DummyProgress';
import ProgressBar from './ProgressBar';
import './ProgressGraph.scss';
import ScoresLabels from './ScoresLabels';
import { MAX_PROGRESS_LENGTH, MIN_PROGRESS_LENGTH } from './constants';

export interface IProgressEntry {
  normalizedScore?: number;
  score?: number;
  time: number;
}

export interface IProgressGraphProps {
  locale: string | undefined;
  noDataLabel: React.ReactNode;
  progress?: IProgressEntry[];
  useNormalizedScore?: boolean;
  useDynamicWidth?: boolean;
  widthPercent?: number;
  scoreRecord?: number;
  showUnderlabel?: boolean;
}

export const ProgressGraph: React.FC<IProgressGraphProps> = ({
  locale,
  noDataLabel,
  progress,
  useNormalizedScore = false,
  useDynamicWidth = false,
  widthPercent,
  scoreRecord,
  showUnderlabel,
}) => {
  const isEmpty = progress && progress.length < MIN_PROGRESS_LENGTH;
  if (!progress || isEmpty || !locale) {
    // show dummy graph with label if not enough data
    return <DummyProgress className={!locale || !progress ? SKELETON_CLASSNAME : ''}>{noDataLabel}</DummyProgress>;
  }
  // limit amount of progress data
  const progressArray = takeRight(progress, MAX_PROGRESS_LENGTH);
  // for normalized score maximum is always 10000
  let maxScore = 10000;
  let labels = ['100%', '75%', '50%', '25%'];
  if (!useNormalizedScore) {
    // calculate labels and maxScore for non normalized data
    const scores = progressArray.map((bar) => bar.score ?? 0);
    maxScore = Math.max(...scores);
    if (scoreRecord && scoreRecord > maxScore) {
      maxScore = scoreRecord;
    }
    labels = [
      `${maxScore}`,
      `${Math.round(maxScore * 0.75)}`,
      `${Math.round(maxScore * 0.5)}`,
      `${Math.round(maxScore * 0.25)}`,
    ];
  }

  // render bars
  const numberOfBars = progressArray.length >= MIN_PROGRESS_LENGTH ? progressArray.length : MIN_PROGRESS_LENGTH;
  const barWidth = useDynamicWidth ? 100 / numberOfBars : widthPercent;
  const bars = progressArray.map((bar) => {
    const date = formatDate(bar.time, locale);
    const score = useNormalizedScore ? bar.normalizedScore : bar.score;
    const label = `${score} | ${date}`;
    const recordClassName = scoreRecord && scoreRecord === score ? 'record' : undefined;
    const underLabel = showUnderlabel ? printDayMonth(bar.time, locale) : undefined;
    return (
      <ProgressBar
        key={bar.time}
        id={bar.time}
        maxScore={maxScore}
        score={score ?? 0}
        label={label}
        widthPercentage={barWidth}
        className={recordClassName}
        underLabel={underLabel}
      />
    );
  });
  const recordLineIndicator = scoreRecord ? 'record' : '';
  return (
    <div className="progress-graph-container">
      <div className="progress-points">
        <ScoresLabels scores={labels} />
      </div>
      <div className="progress-graph">
        <div className="scale">
          <div className={`lines ${recordLineIndicator}`} style={{ bottom: 'calc(100% - 0.2px)' }}></div>
          <div className="lines" style={{ bottom: 'calc(75% - 0.2px)' }}></div>
          <div className="lines" style={{ bottom: 'calc(50% - 0.2px)' }}></div>
          <div className="lines" style={{ bottom: 'calc(25% - 0.2px)' }}></div>
          <div className="lines" style={{ bottom: 0 }}></div>
        </div>
        <div className="progress-bars">{bars}</div>
      </div>
    </div>
  );
};

export default ProgressGraph;
